import { clsx } from 'clsx';
import { forwardRef, useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';
import { ChevronLeft, Loader2, X } from 'lucide-react';

export const cx = clsx;

// ---------------------------------------------------------------- Button

type Variant = 'primary' | 'gold' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand text-brand-ink shadow-[0_8px_20px_-10px_var(--brand)] active:brightness-95',
  gold: 'gold-fill shadow-glow active:brightness-95',
  secondary: 'bg-surface text-ink border border-line active:bg-surface-2',
  ghost: 'bg-transparent text-ink active:bg-surface-2',
  danger: 'bg-danger-soft text-danger active:brightness-95',
};
const SIZE: Record<Size, string> = {
  sm: 'h-9 px-3.5 text-[14px] rounded-xl gap-1.5',
  md: 'h-12 px-5 text-[15px] rounded-2xl gap-2',
  lg: 'h-14 px-6 text-[16px] rounded-2xl gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, block, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cx(
        'inline-flex select-none items-center justify-center font-semibold tracking-[-0.01em] transition-[transform,filter,background-color,opacity] duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-[18px] animate-spin" aria-hidden /> : icon}
      {children && <span className="truncate">{children}</span>}
    </button>
  );
});

// ---------------------------------------------------------------- Card

export function Card({ className, children, as: As = 'div', ...rest }: { className?: string; children: ReactNode; as?: 'div' | 'section' | 'article' } & Record<string, unknown>) {
  return (
    <As className={cx('rounded-2xl bg-surface shadow-card ring-1 ring-line/60', className)} {...rest}>
      {children}
    </As>
  );
}

// ---------------------------------------------------------------- Skeleton

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx('skeleton rounded-xl', className)} />;
}

// ---------------------------------------------------------------- Badge / Chip

type Tone = 'neutral' | 'brand' | 'gold' | 'success' | 'warning' | 'danger';
const TONE: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-2',
  brand: 'bg-brand-soft text-brand',
  gold: 'bg-gold-soft text-gold-ink dark:text-gold-bright',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold leading-none', TONE[tone], className)}>{children}</span>;
}

export function Chip({ selected, children, onClick, className }: { selected?: boolean; children: ReactNode; onClick?: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cx(
        'h-9 shrink-0 rounded-full px-4 text-[14px] font-semibold transition-colors',
        selected ? 'bg-ink text-bg' : 'bg-surface text-ink-2 ring-1 ring-line',
        className,
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------- Segmented

export function Segmented<T extends string>({ value, options, onChange, className }: { value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; className?: string }) {
  return (
    <div role="tablist" className={cx('flex rounded-2xl bg-surface-2 p-1', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          type="button"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={cx(
            'h-10 flex-1 rounded-xl text-[14px] font-semibold transition-all',
            o.value === value ? 'bg-surface text-ink shadow-card' : 'text-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Field

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | null;
  trailing?: ReactNode;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field({ label, hint, error, trailing, className, id, ...rest }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={cx('space-y-1.5', className)}>
      <label htmlFor={inputId} className="block text-[13px] font-semibold text-ink-2">
        {label}
      </label>
      <div
        className={cx(
          'flex h-13 items-center rounded-2xl bg-surface px-4 ring-1 transition-shadow focus-within:ring-2',
          error ? 'ring-danger focus-within:ring-danger' : 'ring-line focus-within:ring-brand',
        )}
      >
        <input ref={ref} id={inputId} className="h-full w-full min-w-0 bg-transparent outline-none placeholder:text-muted/70" aria-invalid={!!error} {...rest} />
        {trailing}
      </div>
      {error ? <p className="text-[13px] font-medium text-danger">{error}</p> : hint ? <p className="text-[13px] text-muted">{hint}</p> : null}
    </div>
  );
});

// ---------------------------------------------------------------- Page header

export function PageHeader({ title, back, action, subtitle }: { title: string; back?: string | true; action?: ReactNode; subtitle?: string }) {
  const nav = useNavigate();
  return (
    <header className="glass sticky top-0 z-30 border-b border-line/60 pt-safe">
      <div className="mx-auto flex h-14 max-w-xl items-center gap-2 px-3">
        {back ? (
          typeof back === 'string' ? (
            <Link to={back} aria-label="Geri" className="grid size-10 place-items-center rounded-full active:bg-surface-2">
              <ChevronLeft className="size-6" />
            </Link>
          ) : (
            <button type="button" onClick={() => nav(-1)} aria-label="Geri" className="grid size-10 place-items-center rounded-full active:bg-surface-2">
              <ChevronLeft className="size-6" />
            </button>
          )
        ) : (
          <span className="w-2" />
        )}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[17px] font-bold tracking-[-0.02em]">{title}</h1>
          {subtitle && <p className="truncate text-[12px] text-muted">{subtitle}</p>}
        </div>
        {action}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------- Empty / Error

export function EmptyState({ icon, title, text, action }: { icon: ReactNode; title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="flex animate-rise flex-col items-center px-6 py-14 text-center">
      <div className="mb-4 grid size-16 place-items-center rounded-3xl bg-brand-soft text-brand">{icon}</div>
      <h3 className="text-[17px] font-bold">{title}</h3>
      {text && <p className="mt-1.5 max-w-xs text-[14px] leading-relaxed text-muted">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Banner({ tone = 'neutral', title, children, action }: { tone?: Tone; title?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cx('flex items-start gap-3 rounded-2xl px-4 py-3 text-[14px] leading-snug', TONE[tone])}>
      <div className="min-w-0 flex-1">
        {title && <p className="font-bold">{title}</p>}
        <div className={cx(title && 'mt-0.5 opacity-90')}>{children}</div>
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------- Bottom sheet

export function Sheet({ open, onClose, title, children, footer, tall }: { open: boolean; onClose: () => void; title?: string; children: ReactNode; footer?: ReactNode; tall?: boolean }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label="Kapat" className="absolute inset-0 animate-fade bg-[var(--backdrop)]" onClick={onClose} />
      <div
        ref={panel}
        tabIndex={-1}
        className={cx(
          'relative flex w-full max-w-xl animate-sheet flex-col rounded-t-[28px] bg-bg shadow-float outline-none',
          tall ? 'h-[92dvh]' : 'max-h-[88dvh]',
        )}
      >
        <div className="mx-auto mt-2.5 h-1.5 w-10 shrink-0 rounded-full bg-line" aria-hidden />
        {title && (
          <div className="flex shrink-0 items-center justify-between px-5 pb-2 pt-3">
            <h2 className="text-[19px] font-bold tracking-[-0.02em]">{title}</h2>
            <button type="button" onClick={onClose} aria-label="Kapat" className="grid size-9 place-items-center rounded-full bg-surface-2 text-muted">
              <X className="size-5" />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4">{children}</div>
        {footer && <div className="shrink-0 border-t border-line/70 px-5 pb-safe pt-3">{footer}<div className="h-3" /></div>}
        {!footer && <div className="pb-safe" />}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------- Misc

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-end justify-between px-1">
      <h2 className="text-[18px] font-bold tracking-[-0.02em]">{children}</h2>
      {action}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <div className={cx('h-px bg-line/70', className)} />;
}

/** Yıldız simgesi (altın). */
export function StarMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <defs>
        <linearGradient id="star-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--gold-bright)" />
          <stop offset="1" stopColor="var(--gold)" />
        </linearGradient>
      </defs>
      <path
        fill="url(#star-g)"
        d="M12 2.6l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 16.6l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z"
      />
    </svg>
  );
}

export function haptic(pattern: number | number[] = 8): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // desteklenmiyor
  }
}
